import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { generate2FASecret, enable2FA, generateBackupCodes } from '../../services/authService';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { QRCodeSVG } from 'qrcode.react';
import toast, { Toaster } from 'react-hot-toast';

const TwoFASetup = () => {
  const navigate = useNavigate();
  const { user, userProfile, userRole, refreshProfile } = useAuth();
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verify, 3: Backup Codes
  const [secret, setSecret] = useState('');
  const [otpauthUrl, setOtpauthUrl] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(true);

  useEffect(() => {
    generateSecret();
  }, []);

  const generateSecret = async () => {
    // Guard: skip only when 2FA is already fully enabled.
    // Do NOT skip when twofa_secret exists but twofa_enabled is false — that
    // means the user started setup, navigated away before verifying, and is
    // now retrying. In that case we must regenerate a fresh secret so they get
    // a valid QR code. The mid-setup remount problem (refreshProfile causing
    // ProtectedRoute to briefly unmount this page) is already solved by
    // deferring refreshProfile() to handleComplete() on step 3.
    if (userProfile?.twofa_enabled) {
      setIsGenerating(false);
      return;
    }

    setIsGenerating(true);
    
    try {
      const result = await generate2FASecret(user?.id, user?.email);

      if (result?.success) {
        setSecret(result?.secret);
        setOtpauthUrl(result?.otpauthUrl);
      } else {
        toast?.error('Failed to generate 2FA secret');
      }
    } catch (error) {
      toast?.error('An error occurred. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault();

    if (!verificationCode || verificationCode?.length !== 6) {
      toast?.error('Please enter a valid 6-digit code');
      return;
    }

    setIsLoading(true);

    try {
      const { verifyTOTP } = await import('../../services/authService');
      const isValid = await verifyTOTP(secret, verificationCode);

      if (!isValid) {
        toast?.error('Invalid code. Please try again.');
        setIsLoading(false);
        return;
      }

      // Generate backup codes
      const codes = generateBackupCodes(10);
      setBackupCodes(codes);

      // Enable 2FA — pass the verification code (not the secret) so the
      // Edge Function can re-verify server-side before writing to the DB.
      const result = await enable2FA(user?.id, verificationCode, codes);

      if (!result?.success) {
        toast?.error(result?.error || 'Failed to enable 2FA');
        setIsLoading(false);
        return;
      }

      // Move to step 3 BEFORE refreshing the profile.
      // refreshProfile() sets profileLoading=true, which causes ProtectedRoute
      // to render the loading spinner, unmounting this component and resetting
      // all state (including step back to 1) — and worse, the remount would
      // call generateSecret() again, overwriting the DB secret with a new one
      // while the user's authenticator still has the old one.
      // The profile refresh is deferred to handleComplete() where navigation
      // away from this page makes the unmount intentional.
      toast?.success('2FA enabled successfully!');
      setStep(3);
    } catch (error) {
      toast?.error('Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleComplete = async () => {
    // Refresh the profile NOW so ProtectedRoute on the destination page sees
    // is2FAEnabled=true. Unmounting this component here is intentional (we're
    // navigating away), so triggering profileLoading is safe at this point.
    await refreshProfile();
    if (userRole === 'admin') {
      navigate('/admin-dashboard');
    } else if (userRole === 'expert') {
      navigate('/expert-dashboard');
    } else {
      navigate('/client-dashboard');
    }
  };

  const handleDownloadBackupCodes = () => {
    const content = `FireCaseIQ - Two-Factor Authentication Backup Codes\n\nGenerated: ${new Date()?.toLocaleString()}\nAccount: ${user?.email}\n\n${backupCodes?.join('\n')}\n\nIMPORTANT: Store these codes in a safe place. Each code can only be used once.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'firecaseiq-backup-codes.txt';
    a?.click();
    URL.revokeObjectURL(url);
  };

  if (isGenerating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-foreground text-lg">Generating 2FA secret...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-secondary/10 px-4 py-12">
      <Toaster position="top-center" />
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary mb-4">
            <Icon name="Shield" className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">Enable Two-Factor Authentication</h1>
          <p className="text-foreground-secondary">Add an extra layer of security to your account</p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 1 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 1 ? <Icon name="Check" className="w-5 h-5" /> : '1'}
            </div>
            <div className={`w-20 h-1 ${
              step >= 2 ? 'bg-primary' : 'bg-gray-200'
            }`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 2 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 2 ? <Icon name="Check" className="w-5 h-5" /> : '2'}
            </div>
            <div className={`w-20 h-1 ${
              step >= 3 ? 'bg-primary' : 'bg-gray-200'
            }`}></div>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full ${
              step >= 3 ? 'bg-primary text-white' : 'bg-gray-200 text-gray-500'
            }`}>
              {step > 3 ? <Icon name="Check" className="w-5 h-5" /> : '3'}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-border">
          {/* Step 1: Scan QR Code */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-foreground mb-2">Step 1: Scan QR Code</h2>
                <p className="text-foreground-secondary mb-6">
                  Use an authenticator app like Google Authenticator or Authy to scan this QR code
                </p>
              </div>

              <div className="flex justify-center">
                <div className="bg-white p-6 rounded-xl border-2 border-border">
                  <QRCodeSVG value={otpauthUrl} size={200} level="H" />
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-foreground mb-2">Can't scan? Enter this code manually:</p>
                <div className="flex items-center justify-between bg-white rounded border border-border p-3">
                  <code className="text-sm font-mono text-foreground break-all">{secret}</code>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(secret);
                      toast?.success('Secret copied to clipboard');
                    }}
                    className="ml-2 text-primary hover:text-primary/80"
                  >
                    <Icon name="Copy" className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <Button
                onClick={() => setStep(2)}
                className="w-full"
              >
                Continue to Verification
              </Button>
            </div>
          )}

          {/* Step 2: Verify Code */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center">
                <h2 className="text-xl font-bold text-foreground mb-2">Step 2: Verify Code</h2>
                <p className="text-foreground-secondary mb-6">
                  Enter the 6-digit code from your authenticator app
                </p>
              </div>

              <form onSubmit={handleVerify} className="space-y-6">
                <div>
                  <Input
                    type="text"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e?.target?.value?.replace(/\D/g, '')?.slice(0, 6))}
                    placeholder="000000"
                    maxLength={6}
                    className="w-full text-center text-2xl tracking-widest"
                    autoFocus
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    type="button"
                    onClick={() => setStep(1)}
                    variant="outline"
                    className="flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    disabled={isLoading || verificationCode?.length !== 6}
                    className="flex-1"
                  >
                    {isLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Step 3: Backup Codes */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4">
                  <Icon name="CheckCircle" className="w-8 h-8 text-success" />
                </div>
                <h2 className="text-xl font-bold text-foreground mb-2">2FA Enabled Successfully!</h2>
                <p className="text-foreground-secondary mb-6">
                  Save these backup codes in a safe place. You can use them to access your account if you lose your device.
                </p>
              </div>

              <div className="bg-warning/5 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start">
                  <Icon name="AlertTriangle" className="w-5 h-5 text-warning mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-foreground-secondary">
                    <p className="font-medium text-foreground mb-1">Important</p>
                    <p>Each backup code can only be used once. Store them securely and don't share them with anyone.</p>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-6">
                <div className="grid grid-cols-2 gap-3">
                  {backupCodes?.map((code, index) => (
                    <div key={index} className="bg-white rounded border border-border p-3 text-center">
                      <code className="text-sm font-mono text-foreground">{code}</code>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleDownloadBackupCodes}
                  variant="outline"
                  className="flex-1"
                >
                  <Icon name="Download" className="w-5 h-5 mr-2" />
                  Download Codes
                </Button>
                <Button
                  onClick={handleComplete}
                  className="flex-1"
                >
                  Continue to Dashboard
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TwoFASetup;